import { faBluesky } from '@fortawesome/free-brands-svg-icons/faBluesky';
import { faDiscord } from '@fortawesome/free-brands-svg-icons/faDiscord';
import { faMastodon } from '@fortawesome/free-brands-svg-icons/faMastodon';
import { faTelegramPlane } from '@fortawesome/free-brands-svg-icons/faTelegramPlane';
import { faTwitter } from '@fortawesome/free-brands-svg-icons/faTwitter';
import { faHeart } from '@fortawesome/pro-solid-svg-icons/faHeart';
import clsx from 'clsx';
import type { FC } from 'react';
import { Col, Container, Nav, Row } from 'react-bootstrap';
import { Trans, useTranslation } from 'react-i18next';

import FontAwesomeIcon from '#/components/FontAwesomeIcon.tsx';
import Placeholder from '@/components/Placeholder.tsx';
import {
  DISCORD_INVITE_URL,
  FURALITY_BLUESKY,
  FURALITY_CODE_OF_CONDUCT,
  FURALITY_DISCORD_SUPPORT,
  FURALITY_MAIN_WEBSITE_LINK,
  FURALITY_MASTODON,
  FURALITY_PRIVACY_POLICY,
  FURALITY_TELEGRAM,
  FURALITY_TWITTER,
} from '@/lib/constants.ts';
import useLoading from '@/lib/hooks/useLoading.ts';
import useSupportModal from '@/lib/hooks/useSupportModal.ts';

import styles from './Footer.module.scss';

type FooterProps = {
  backgroundColor?: string;
};

export const Footer: FC<FooterProps> = () => {
  const { t } = useTranslation();

  const [, setShowSupportModal] = useSupportModal();

  const loading = useLoading();

  return (
    <footer className="footer p-0 footer-dark">
      <Container className="py-4">
        <Row className="row-grid align-items-center mb-sm-5 mt-sm-3">
          <Col sm={5} md={7} lg={8}>
            <div className={clsx('media mx-auto mx-sm-0', styles['logo'])}>
              <img
                alt="Furality Inc Logo"
                src="https://media.cdn.furality.online/image/logo/furality-logo.svg"
                className="img-fluid"
              />
            </div>
          </Col>
          <Col sm={7} md={5} lg={4} className="text-center text-sm-left my-4 my-sm-auto">
            <Row>
              <Col xs={12} sm={4}>
                <Nav as="ul" className="d-block">
                  <Nav.Item as="li">
                    <Nav.Link as="span">
                      {loading ? (
                        <Placeholder width="4em" />
                      ) : (
                        <button
                          type="button"
                          className="unstyled-button"
                          onClick={() => setShowSupportModal(true)}
                        >
                          {t('shared.footer.support')}
                        </button>
                      )}
                    </Nav.Link>
                  </Nav.Item>
                </Nav>
              </Col>
              <Col xs={12} sm={4}>
                <Nav as="ul" className="d-block">
                  <Nav.Item as="li">
                    <Nav.Link href={FURALITY_CODE_OF_CONDUCT}>
                      {loading ? <Placeholder width="4em" /> : t('shared.footer.code_of_conduct')}
                    </Nav.Link>
                  </Nav.Item>
                </Nav>
              </Col>
              <Col xs={12} sm={4}>
                <Nav as="ul" className="d-block">
                  <Nav.Item as="li">
                    <Nav.Link href={FURALITY_PRIVACY_POLICY}>
                      {loading ? <Placeholder width="4em" /> : t('shared.footer.privacy_policy')}
                    </Nav.Link>
                  </Nav.Item>
                </Nav>
              </Col>
            </Row>
          </Col>
        </Row>
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 border-top footer-border-gradient border-1 pt-3">
          <div className="text-center text-md-left col-12 col-md-auto">
            <p className="text-sm mb-0">
              {loading ? (
                <Placeholder width="30em" />
              ) : (
                <Trans
                  i18nKey="shared.footer.copyright"
                  values={{ year: new Date().getFullYear(), version: '3.1' }}
                />
              )}
              <br />
              {loading ? (
                <Placeholder width="20em" />
              ) : (
                <Trans
                  i18nKey="shared.footer.credits"
                  components={{
                    linkToFurality: <a href={FURALITY_MAIN_WEBSITE_LINK} className="text-white" />,
                    furalityDevTeamModal: (
                      <a className="text-white" href="https://furality.org/team#DevOps" />
                    ),
                  }}
                />
              )}
            </p>
          </div>
          <Nav as="ul" className="justify-content-center col gap-3 col-xl-auto">
            <Nav.Item as="li">
              <Nav.Link
                href={DISCORD_INVITE_URL}
                target="_blank"
                rel="noreferrer"
                className="p-0 width-3"
              >
                <FontAwesomeIcon icon={faDiscord} />
                <span className="visually-hidden">{t('shared.footer.discord')}</span>
              </Nav.Link>
            </Nav.Item>
            <Nav.Item as="li">
              <Nav.Link href={FURALITY_TWITTER} target="_blank" rel="noreferrer" className="p-0">
                <FontAwesomeIcon icon={faTwitter} />
                <span className="visually-hidden">{t('shared.footer.twitter')}</span>
              </Nav.Link>
            </Nav.Item>
            <Nav.Item as="li">
              <Nav.Link href={FURALITY_TELEGRAM} target="_blank" rel="noreferrer" className="p-0">
                <FontAwesomeIcon icon={faTelegramPlane} />
                <span className="visually-hidden">{t('shared.footer.telegram')}</span>
              </Nav.Link>
            </Nav.Item>
            <Nav.Item as="li">
              <Nav.Link href={FURALITY_BLUESKY} rel="me" className="p-0">
                <FontAwesomeIcon icon={faBluesky} />
                <span className="visually-hidden">{t('shared.footer.bluesky')}</span>
              </Nav.Link>
            </Nav.Item>
            <Nav.Item as="li">
              <Nav.Link href={FURALITY_MASTODON} rel="me" className="p-0">
                <FontAwesomeIcon icon={faMastodon} />
                <span className="visually-hidden">{t('shared.footer.mastodon')}</span>
              </Nav.Link>
            </Nav.Item>
            <Nav.Item as="li">
              <Nav.Link href={FURALITY_DISCORD_SUPPORT} rel="me" className="p-0">
                <FontAwesomeIcon icon={faHeart} />
                <span className="visually-hidden">{t('shared.footer.support_us_discord')}</span>
              </Nav.Link>
            </Nav.Item>
          </Nav>
        </div>
      </Container>
    </footer>
  );
};

export default Footer;
